import {
	Component,
	OnInit,
	ChangeDetectorRef,
	Input,
	HostListener,
	Renderer2,
	ViewChild,
	ElementRef,
	OnDestroy,
	OnChanges,
	Output,
	EventEmitter
} from '@angular/core';

import WaveSurfer from 'wavesurfer.js';
import { AudioPlayerOptions } from '../utilities/models/audio-player-options-model';
import { DetectDeviceService } from '@app/system/services/detect-device.service';
import { GlobalFunctionService } from '@app/system/services/global-function.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
	selector: 'app-audio-player',
	templateUrl: './audio-player.component.html',
	styleUrls: ['./audio-player.component.scss']
})
export class AudioPlayerComponent implements OnInit, OnChanges, OnDestroy {

	@Input() source: string;
	@Input() options: AudioPlayerOptions;
	@Input() canStartPlay: boolean = false;
	@Input() currentTime: string;
	@Input() segmentsProcess: boolean = false;

	@Output() segments = new EventEmitter();
	@Output() updatedTime = new EventEmitter();

	@ViewChild('volumeControlEl') volumeControlEl: ElementRef;
	volumeControlNativeEl;
	@ViewChild('volumeBarEl') volumeBarEl: ElementRef;
	volumeBarNativeEl;
	@ViewChild('volumeProgressEl') volumeProgressEl: ElementRef;
	volumeProgressNativeEl;
	@ViewChild('volumePointerEl') volumePointerEl: ElementRef;
	volumePointerNativeEl;

	wave: WaveSurfer = null;
	audioDuration = '';
	isPlaying = false;
	showVolumeBar = false;
	currentVolume = 1;
	loader = true;
	defaultOptions: AudioPlayerOptions = {
		skip: true,
		waveColor: '#9e9e9e',
		progressColor: '#0091ea',
		responsive: true
	};
	volumeBarBounds = null;

	documentListenMM;
	documentListenMU;

	volumeDragState = false;
	volumeBarState = false;
	subscribe;
	showExtraBookmark = false;
	expanded = false;
	bookMarksData: any = {};
	currentBookmarkIndex = 0;
	showBookmarkPagination = false;
	currentTimelineIndex = 0;
	totalDuration;
	disableForward = false;
	totalSegments = 0;
	segmentSplitValue = 10;
	segmentData = [];
	segmentedData: Array<Number> = [];
	oldSegmentIndex;

	constructor(
		private cdr: ChangeDetectorRef,
		private renderer2: Renderer2,
		public detectDevice: DetectDeviceService,
		private gfService: GlobalFunctionService,
		private iconRegistry: MatIconRegistry,
		private sanitizer: DomSanitizer,
		private cdref: ChangeDetectorRef
	) {
		iconRegistry.addSvgIcon('forward', sanitizer.bypassSecurityTrustResourceUrl('/assets/svg/forward.svg'));
		iconRegistry.addSvgIcon('rewind', sanitizer.bypassSecurityTrustResourceUrl('/assets/svg/rewind.svg'));
		this.subscribe = this.detectDevice.resolutionState$.subscribe((device) => {
			if (this.volumeControlNativeEl) {
				this.volumeBarBounds = this.volumeControlNativeEl.getBoundingClientRect();
			}
		});
	}

	ngOnInit(): void {
		this.options = Object.assign(this.defaultOptions, this.options);
		this.options['interact'] = false;
		this.loadAudioWave();
	}

	ngOnChanges(changes) {
		if (changes) {
			if (changes.hasOwnProperty('canStartPlay') && changes.canStartPlay.currentValue) {
				if (this.wave && !this.options['interact']) {
					this.options['interact'] = true;
					this.wave.toggleInteraction();
				}
			}
		}
	}

	ngAfterViewInit() {
		this.volumeControlNativeEl = this.volumeControlEl.nativeElement;
		this.volumeBarNativeEl = this.volumeBarEl.nativeElement;
		this.volumeBarBounds = this.volumeBarNativeEl.getBoundingClientRect();
		this.volumePointerNativeEl = this.volumePointerEl.nativeElement;
		this.volumeProgressNativeEl = this.volumeProgressEl.nativeElement;
		this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `${this.volumeBarNativeEl.offsetHeight}px`);
		this.renderer2.setStyle(
			this.volumePointerNativeEl,
			'transform',
			`translateY(-${this.volumeBarNativeEl.offsetHeight}px)`
		);
	}

	showMoreBookmarks() {
		// this.renderer2.setStyle();
		this.expanded = !this.expanded;
	}

	loadAudioWave(): void {
		if (!this.wave) {
			this.generateWaveform();
		}
		this.cdr.detectChanges();
		Promise.resolve().then(() => this.wave.load(this.source));
	}

	generateWaveform(): void {
		Promise.resolve(null).then(() => {
			this.wave = WaveSurfer.create(
				this.gfService.JSONMerge(
					{
						container: '#waveform',
						barWidth: 2,
						barHeight: 1, // the height of the wave
						barGap: null, // the optional spacing between bars of the wave, if not provided will be calculated in legacy format
						skipLength: 10,
						backend: 'MediaElement'
					},
					this.options
				)
			);

			this.wave.on('ready', () => {
				if (this.currentTime) {
					this.wave.skip(this.gfService.getTimeToSeconds(this.currentTime, { timeFormat: 'hh:mm:ss' }));
				}
				// this.wave.play();
				// this.wave.playPause();
				this.wave.setVolume(1);
				this.updateTimer();
				this.totalDuration = this.formatTime(this.wave.getDuration());
				if (this.segmentsProcess) {
					this.totalSegments = Math.round(this.wave.getDuration() / this.segmentSplitValue);
					if (this.totalSegments) {
						for (let i = 1; i <= this.totalSegments; i++) {
							let start = (i - 1) * this.segmentSplitValue;
							let end = i * this.segmentSplitValue;
							if (i === this.totalSegments) {
								end = this.wave.getDuration();
							}
							this.segmentData.push(
								{
									start,
									end
								}
							);
						}
					}
				}
				// if (![null, undefined].includes(this.segment) && this.segmentData.length && this.segmentData[this.segment - 1]) {
				// 	this.wave.skip(this.segmentData[this.segment - 1].start);
				// }
				let scope = this;
				this.wave.on('ready', this.updateTimer.bind(this));
				this.wave.on('audioprocess', this.updateTimer.bind(this));
				this.wave.on('waveform-ready', this.waveLoaded.bind(this));
				this.wave.on('finish', this.finished.bind(this));

				// Need to watch for seek in addition to audioprocess as audioprocess doesn't fire
				// if the audio is paused.
				this.wave.on('seek', this.updateTimer.bind(this));
				this.cdr.detectChanges();
			});
		});
	}

	waveLoaded() {
		this.loader = false;
		this.cdr.detectChanges();
		if (!this.options['interact'] && this.canStartPlay) {
			this.options['interact'] = true;
			this.wave.toggleInteraction();
		}
	}

	setBookmarkTime(data) {
		if (data) {
		}
	}

	changeBookmark(action: 'before' | 'after') {
		if (navigator.onLine) {
			if (action === 'after' && this.currentTimelineIndex !== this.bookMarksData[this.currentBookmarkIndex].timelineData.length - 1) {
				this.currentTimelineIndex += 1;
				this.setBookmarkTime(this.bookMarksData[this.currentBookmarkIndex].timelineData[this.currentTimelineIndex]);
			} else if (action === 'before' && this.currentTimelineIndex !== 0) {
				this.currentTimelineIndex -= 1;
				this.setBookmarkTime(this.bookMarksData[this.currentBookmarkIndex].timelineData[this.currentTimelineIndex]);
			}
		}
	}

	removeBookMarkSelection(data) {
		if (navigator.onLine) {
			this.currentBookmarkIndex = 0;
			this.showBookmarkPagination = false;
		}
	}

	@HostListener('contextmenu', ['$event'])
	preventContext(event) {
		event.preventDefault();
	}

	formatTime(timeInSeconds, timeFormat = 'h:m:ss') {
		// Hours, minutes and seconds
		const hrs = ~~(timeInSeconds / 3600);
		const mins = ~~((timeInSeconds % 3600) / 60);
		const secs = ~~timeInSeconds % 60;
		const formats = timeFormat.split(':');

		let ret = '';
		if (hrs > 0) {
			const hours = (formats.includes('hh')) ? ((hrs < 10) ? `0${hrs}` : hrs) : hrs;
			ret += '' + hours + ':';
		} else if (formats.includes('hh')) {
			ret += '00:';
		}

		const minutes = (formats.includes('mm')) ? ((mins < 10) ? `0${mins}` : mins) : mins;

		const seconds = (formats.includes('ss')) ? ((secs < 10) ? `0${secs}` : secs) : secs;

		ret += '' + minutes + ':' + seconds;

		return ret;
	}

	updateTimer() {
		this.audioDuration = this.formatTime(this.wave.getCurrentTime());
		const formattedTime = this.formatTime(Math.round(this.wave.getCurrentTime()), 'hh:mm:ss');
		if (this.segmentsProcess) {
			this.processSegment(formattedTime);
		}
		if (!this.wave.isPlaying()) {
			this.isPlaying = false;
		}
		this.cdr.detectChanges();
		this.updatedTime.emit(this.wave.getCurrentTime());
	}

	processSegment(currentTime) {
		const index = this.segmentData.findIndex(items => items.start <= this.wave.getCurrentTime() && items.end >= this.wave.getCurrentTime());
		if (index !== -1 && ([null, undefined].includes(this.oldSegmentIndex) || this.oldSegmentIndex !== index)) {
			this.segmentedData.push(index + 1);
			this.segmentedData.sort();
			this.segments.emit(
				{
					totalSegment: this.totalSegments,
					segments: this.segmentedData,
					currentSegment: index + 1,
					currentTime
				}
			);
			this.oldSegmentIndex = index;
		}
	}

	finished() {
		this.disableForward = true;
	}

	tooglePlay() {
		if (navigator.onLine) {
			if (this.wave.isPlaying()) {
				this.isPlaying = false;
				this.wave.pause();
			} else {
				this.isPlaying = true;
				this.wave.playPause();
			}
		}
	}

	replay() {
		if (navigator.onLine) {
			this.wave.stop();
			this.wave.play();
			this.isPlaying = true;
			this.cdr.detectChanges();
			this.disableForward = false;
		}
	}

	forward() {
		if (navigator.onLine) {
			if (this.wave.getCurrentTime() + 10 >= this.wave.getDuration()) {
				this.wave.pause();
			}
			const currentTime: number = this.wave.getCurrentTime();
			const totalDuration: number = this.wave.getDuration();
			if (currentTime.toFixed(2) !== totalDuration.toFixed(2)) {
				this.wave.skipForward();
			} else {
				this.isPlaying = false;
			}
			if (this.wave.getCurrentTime().toFixed(2) === this.wave.getDuration().toFixed(2)) {
				this.disableForward = true;
			}
		}
	}

	backward() {
		if (navigator.onLine) {
			this.disableForward = false;
			if (this.wave.getCurrentTime() !== 0) {
				this.wave.skipBackward();
			} else {
				this.isPlaying = false;
			}
		}
	}

	mute() {
		this.wave.toggleMute();
		if (this.wave.getMute()) {
			this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `0px`);
			this.renderer2.setStyle(this.volumePointerNativeEl, 'transform', `translateY(0px)`);
		} else {
			const posY = this.volumeBarNativeEl.offsetHeight * this.currentVolume;
			this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `${posY}px`);
			this.renderer2.setStyle(this.volumePointerNativeEl, 'transform', `translateY(-${posY}px)`);
		}
	}

	volumeControl(event) {
		if (event && this.canStartPlay) {
			this.volumeBarBounds = this.volumeBarNativeEl.getBoundingClientRect();
			let posY = this.volumeBarNativeEl.offsetHeight - (event.clientY - this.volumeBarBounds.top);
			if (posY < 0) posY = 0;
			if (posY > this.volumeBarNativeEl.offsetHeight) {
				posY = this.volumeBarNativeEl.offsetHeight;
			}
			const scrubTime = posY / this.volumeBarNativeEl.offsetHeight;
			let volumePercentage = Math.floor(scrubTime / 1 * 100);
			if (volumePercentage < 0) {
				volumePercentage = 0;
			}
			this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `${posY}px`);
			this.renderer2.setStyle(this.volumePointerNativeEl, 'transform', `translateY(-${posY}px)`);
			if (volumePercentage >= 0 && volumePercentage <= 100) {
				const volume = volumePercentage / 100;
				this.wave.setVolume(volume);
				this.currentVolume = volume;
				if (volume == 0) {
					this.wave.setMute(true);
				} else {
					this.wave.setMute(false);
				}
			}
		}
	}
	onVolumeMouseOver(event) {
		if (this.canStartPlay) {
			this.volumeBarState = true;
			const volumeControlCSS = getComputedStyle(this.volumeControlNativeEl);
			if (volumeControlCSS.visibility === 'hidden')
				this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `visible`);
		}
	}
	onVolumeMouseLeave(event) {
		if (this.canStartPlay) {
			this.volumeBarState = false;
			const volumeBarCSS = getComputedStyle(this.volumeControlNativeEl);
			if (volumeBarCSS.visibility === 'visible')
				this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `hidden`);
		}
	}
	volumePointerMouseDown(event) {
		if (this.canStartPlay) {
			event.preventDefault();
			this.onVolumeDragStart();
			this.documentListenMM = this.renderer2.listen(document, 'mousemove', this.onVolumeDrag.bind(this));
			this.documentListenMU = this.renderer2.listen(document, 'mouseup', this.onVolumeDragEnd.bind(this));
		}
	}

	onVolumeDragStart() {
		if (this.canStartPlay) {
			this.volumeDragState = true;
		}
	}
	onVolumeDrag(event: DragEvent) {
		if (this.canStartPlay) {
			const volumeBarCSS = getComputedStyle(this.volumeControlNativeEl);
			if (volumeBarCSS.visibility === 'hidden')
				this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `visible`);
			this.volumeControl(event);
		}
	}
	onVolumeDragEnd(event) {
		if (this.canStartPlay) {
			this.volumeDragState = false;
			this.documentListenMU();
			this.documentListenMM();
			const volumeBarCSS = getComputedStyle(this.volumeControlNativeEl);
			if (!this.volumeBarState) {
				if (volumeBarCSS.visibility === 'visible')
					this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `hidden`);
			}
		}
	}

	ngOnDestroy() {
		this.wave.destroy();
		if (this.subscribe) {
			this.subscribe.unsubscribe();
		}
	}
}
